import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { InterviewPreview } from './interview-preview';
import {
  renderPreview,
  type TemplateVariableKey,
  TemplateVariableButtons
} from './template-variables';

import { Button } from '~/components/ui/button';
import { Textarea } from '~/components/ui/textarea';
import { api } from '~/lib/api';
import { communicationsQueryOptions } from '~/lib/communications';

const INTERVIEW_VARIABLES: TemplateVariableKey[] = [
  'recruiterName',
  'applicantName',
  'jobTitle',
  'jobEmployer'
];

interface InterviewMessageTemplateEditorProps {
  orgId: string;
  deptId: string;
  templateType: 'intro' | 'outro';
  lang: string;
}

export function InterviewMessageTemplateEditor({
  orgId,
  deptId,
  templateType,
  lang
}: InterviewMessageTemplateEditorProps): React.ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { data, isLoading } = useQuery(communicationsQueryOptions(orgId, deptId, lang));
  const templateData = data?.[templateType];

  const [body, setBody] = useState<string>(templateData?.body ?? '');

  useEffect(() => {
    if (templateData) setBody(templateData.body);
  }, [templateData?.body]);

  const invalidate = (): void => {
    void queryClient.invalidateQueries({
      queryKey: ['communications', 'templates', orgId, deptId]
    });
  };

  function insertVariable(variable: string): void {
    const el = textareaRef.current;
    if (!el) {
      setBody((prev) => prev + variable);
      return;
    }
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const next = body.slice(0, start) + variable + body.slice(end);
    setBody(next);
    requestAnimationFrame(() => {
      el.selectionStart = start + variable.length;
      el.selectionEnd = start + variable.length;
      el.focus();
    });
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      const res = await api.api.org[':orgId'].department[':deptId'].communications[
        'interview-message-templates'
      ].$put({
        param: { orgId, deptId },
        json: { departmentId: deptId, type: templateType, language: lang, body }
      });
      if (!res.ok) throw new Error(t('errors.unexpected'));
    },
    onSuccess: () => {
      invalidate();
      toast.success(t('communications.actions.saved'));
    },
    onError: () => toast.error(t('errors.unexpected'))
  });

  const resetMutation = useMutation({
    mutationFn: async () => {
      const id = templateData?.id;
      if (!id) return;
      const res = await api.api.org[':orgId'].department[':deptId'].communications[
        'interview-message-templates'
      ][':templateId'].$delete({
        param: { orgId, deptId, templateId: id }
      });
      if (!res.ok) throw new Error(t('errors.unexpected'));
    },
    onSuccess: () => {
      invalidate();
      toast.success(t('communications.actions.resetDone'));
    },
    onError: () => toast.error(t('errors.unexpected'))
  });

  const isSaving = saveMutation.isPending;
  const isResetting = resetMutation.isPending;
  const isCustomized = templateData?.isCustomized ?? false;
  const isUnchanged = body === templateData?.body;

  return (
    <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
      {/* Editor */}
      <div className="flex flex-col gap-4">
        {isLoading ? (
          <div className="h-48 animate-pulse rounded-lg bg-muted" />
        ) : (
          <div className="flex flex-col gap-2">
            <label
              className="text-sm font-medium text-foreground"
              htmlFor="interview-template-body"
            >
              {t(`communications.${templateType}.scriptLabel`)}
            </label>
            <Textarea
              ref={textareaRef}
              id="interview-template-body"
              value={body}
              onChange={(e) => {
                setBody(e.target.value);
              }}
              disabled={isSaving || isResetting}
              placeholder={t(`communications.${templateType}.scriptPlaceholder`)}
              rows={6}
              className="resize-none"
            />
            <TemplateVariableButtons
              onInsert={insertVariable}
              disabled={isSaving || isResetting}
              variables={INTERVIEW_VARIABLES}
            />
          </div>
        )}

        <div className="flex items-center justify-between pt-2">
          <Button
            variant="secondary"
            onClick={() => {
              if (!isCustomized) setBody(templateData?.body ?? '');
              resetMutation.mutate();
            }}
            disabled={isResetting || isSaving || !isCustomized}
          >
            {t('communications.actions.reset')}
          </Button>
          <Button
            onClick={() => {
              saveMutation.mutate();
            }}
            disabled={isSaving || isUnchanged}
          >
            {t('communications.actions.save')}
          </Button>
        </div>
      </div>

      {/* Preview */}
      <div className="hidden lg:flex lg:flex-col lg:items-center lg:pt-2">
        <InterviewPreview body={renderPreview(body)} variant={templateType} />
      </div>
    </div>
  );
}
